import { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
    apiLogin,
    apiLogout,
    apiMe,
    apiSignup,
    getStoredToken,
    setStoredToken,
} from "@/lib/api";

const AuthContext = createContext({
    user: null,
    loading: true,
    signup: async () => null,
    login: async () => null,
    logout: async () => null,
    refresh: async () => null,
    openAuthModal: () => {},
    closeAuthModal: () => {},
    authModalOpen: false,
    authModalMode: "login",
});

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [authModalOpen, setAuthModalOpen] = useState(false);
    const [authModalMode, setAuthModalMode] = useState("login"); // "login" | "signup"

    const refresh = useCallback(async () => {
        try {
            const me = await apiMe();
            setUser(me);
            return me;
        } catch (_e) {
            setUser(null);
            return null;
        }
    }, []);

    // On mount: try restoring session
    useEffect(() => {
        let mounted = true;
        (async () => {
            const tok = getStoredToken();
            // Even without a token, the cookie alone might be valid
            const me = await refresh();
            if (!mounted) return;
            if (!me && tok) setStoredToken(null);
            setLoading(false);
        })();
        return () => { mounted = false; };
    }, [refresh]);

    const signup = useCallback(async (email, password) => {
        const data = await apiSignup(email, password);
        setUser(data.user);
        return data.user;
    }, []);

    const login = useCallback(async (email, password) => {
        const data = await apiLogin(email, password);
        setUser(data.user);
        return data.user;
    }, []);

    const logout = useCallback(async () => {
        await apiLogout();
        setUser(null);
    }, []);

    const openAuthModal = useCallback((mode = "login") => {
        setAuthModalMode(mode);
        setAuthModalOpen(true);
    }, []);
    const closeAuthModal = useCallback(() => setAuthModalOpen(false), []);

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                signup,
                login,
                logout,
                refresh,
                openAuthModal,
                closeAuthModal,
                authModalOpen,
                authModalMode,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
